import createClient, { type Middleware } from "openapi-fetch";
import { lang, t } from "../i18n";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type Me = Schemas["MeOut"];
export type Person = Schemas["PersonOut"];
export type TeamSummary = Schemas["TeamSummary"];
export type TeamDetail = Schemas["TeamDetail"];
// A team's "what a member experiences" preview (the file previewer's own PreviewOut took the
// plain name's place in the schema, R8 task 10).
export type Preview = Schemas["app__api__teams__PreviewOut"];
export type Resource = Schemas["ResourceOut"];
export type SettingsOut = Schemas["SettingsOut"];
export type Level = "none" | "read" | "write" | "manage";
export type Tool = "redmine" | "github" | "outline";

const SAFE = new Set(["GET", "HEAD", "OPTIONS"]);

/** The headers every call to the API carries: the language the web shows (R10-B11: the
 *  server's refusals come back in it), and on a write X-VX-Client: web (the CSRF defense,
 *  PLAN §6.3). The raw fetch helpers in api/ spread this too. */
export function webHeaders(method = "GET"): Record<string, string> {
  const headers: Record<string, string> = { "X-VX-Lang": lang() };
  if (!SAFE.has(method.toUpperCase())) headers["X-VX-Client"] = "web";
  return headers;
}

const clientHeader: Middleware = {
  onRequest({ request }) {
    for (const [name, value] of Object.entries(webHeaders(request.method)))
      request.headers.set(name, value);
    return request;
  },
};

export const api = createClient<paths>({
  baseUrl: typeof window === "undefined" ? "http://localhost" : window.location.origin,
  credentials: "same-origin",
});
api.use(clientHeader);

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The rest of the error body (e.g. a leak's `missing` labels, R6 contract 12). */
    readonly detail: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

type Result<T> = { data?: T; error?: unknown; response: Response };

/** What a person reads when Vantage couldn't be reached at all (R10-S2), never the browser's
 *  own "Load failed" or "Failed to fetch". */
export function offline(): string {
  return t("common.offline");
}

/** A failure as one plain sentence: the server's message, or the connection's. */
export function errorText(e: unknown, fallback?: string): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof TypeError) return offline(); // fetch's network error
  return fallback ?? t("common.somethingWrong");
}

/** Throws ApiError for non-2xx answers ({code, message} bodies), and for no answer at all
 *  (status 0, code `offline`); returns the data otherwise. */
export async function unwrap<T>(call: Promise<Result<T>>): Promise<T> {
  let result: Result<T>;
  try {
    result = await call;
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "offline", offline());
  }
  const { data, error, response } = result;
  if (!response.ok) {
    const body = (error && typeof error === "object" ? error : {}) as Record<string, unknown>;
    throw new ApiError(
      response.status,
      typeof body.code === "string" ? body.code : "error",
      typeof body.message === "string" ? body.message : t("common.somethingWrong"),
      body,
    );
  }
  return data as T;
}
